/**
 * Where the reader is, in the top bar: "Admin › Users", "Leaderboard › Details".
 *
 * Read from the same nav config as the sidebar, so a page never has to announce
 * its own location. On a phone only the last crumb fits, and it is the one that
 * matters; the group is one tap away in the sidebar anyway.
 *
 * A page deeper than "a detail of a list" - a concept's presentation, one of
 * its lines, one shot - pushes its own trail (`usePageCrumbs`), which replaces
 * the generic "Details": "Pipeline › Concepts › CPT-3993 › Line 3 › S07".
 */
import { Fragment, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { findActive } from "@/components/layout/nav-config";
import { usePushedCrumbs, type PageCrumb } from "@/components/layout/page-crumbs";
import { useSession } from "@/hooks/use-session";

/** index.html's title, as the page first loaded. */
const DEFAULT_TITLE = document.title;

export function PageBreadcrumb() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { role, studio } = useSession();
  const pushed = usePushedCrumbs();
  const active = findActive(pathname, role, { studio });
  const { crumbs, title } = active ? trailOf(active, pathname, pushed, t) : { crumbs: null, title: null };

  // The browser tab says where the reader is: "Users · Scandi", "Details · Leaderboard
  // · Scandi". A page that pushes its own trail (a concept's presentation, a line)
  // names its own tab, so nothing is set while a pushed trail is showing.
  // A page outside the nav gets the app's own name back.
  const tab = title ? `${title} · Scandi` : active ? null : DEFAULT_TITLE;
  useEffect(() => {
    if (tab) document.title = tab;
  }, [tab]);

  if (!crumbs) return null;

  return (
    <Breadcrumb aria-label={t("nav.breadcrumb")} className="min-w-0">
      <BreadcrumbList className="flex-nowrap">
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <Fragment key={index}>
              <BreadcrumbItem
                className={last ? "min-w-0" : crumb.wide ? "hidden lg:inline-flex" : "hidden sm:inline-flex"}
              >
                {last ? (
                  <BreadcrumbPage className="truncate font-medium">{crumb.label}</BreadcrumbPage>
                ) : crumb.to ? (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.to}>{crumb.label}</Link>
                  </BreadcrumbLink>
                ) : (
                  <span className="whitespace-nowrap">{crumb.label}</span>
                )}
              </BreadcrumbItem>
              {!last && (
                <BreadcrumbSeparator className={crumb.wide ? "hidden lg:inline-flex" : "hidden sm:inline-flex"} />
              )}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

type Crumb = { label: string; to?: string; wide?: boolean };

/** The breadcrumb's crumbs for this page, and the tab title (null when the page names its own). */
function trailOf(
  active: NonNullable<ReturnType<typeof findActive>>,
  pathname: string,
  pushed: readonly PageCrumb[] | null,
  t: TFunction,
): { crumbs: Crumb[]; title: string | null } {
  const { group, item } = active;
  // A detail page (one editor, one creative, one conversation) sits under the
  // list it was opened from, which becomes a link back to that list.
  const isDetail = pathname !== item.to;

  const trail = isDetail && pushed !== null && pushed.length > 0;
  // `wide`: shown only from `lg` up. A pushed trail is long, so the group
  // ("Pipeline") gives way first; the trail itself is what says where you are.
  const crumbs: Crumb[] = [];
  if (group) crumbs.push({ label: t(group.label), wide: trail });
  crumbs.push({ label: t(item.label), to: isDetail ? item.to : undefined });
  if (trail) {
    // The page's own trail. Its last crumb is where the reader is, whatever
    // link the page gave it.
    pushed.forEach((crumb, index) =>
      crumbs.push(index === pushed.length - 1 ? { label: crumb.label } : crumb),
    );
  } else if (isDetail) {
    crumbs.push({ label: t("nav.details") });
  }

  const title = trail ? null : [isDetail ? t("nav.details") : null, t(item.label)].filter(Boolean).join(" · ");
  return { crumbs, title };
}
